import { useEffect, useState } from 'react'

interface MobileDetection {
  isMobile: boolean
  isIOS: boolean
  isAndroid: boolean
  isLineAppLikely: boolean
  isFacebookAppLikely: boolean
}

export function useMobileDetection(): MobileDetection {
  const [detection, setDetection] = useState<MobileDetection>({
    isMobile: false,
    isIOS: false,
    isAndroid: false,
    isLineAppLikely: false,
    isFacebookAppLikely: false,
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Safe navigator access
    const userAgent = String(window.navigator?.userAgent || '').toLowerCase()
    const platform = String(
      (window.navigator as Navigator & { platform?: string })?.platform || ''
    ).toLowerCase()
    
    // Detect mobile
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
    
    // Detect iOS
    const isIOS = /iphone|ipad|ipod/i.test(userAgent) || 
                  (platform.includes('mac') && 'ontouchend' in document)
    
    // Detect Android
    const isAndroid = /android/i.test(userAgent)
    
    // LINE is extremely popular in Thailand - assume it's installed on most devices
    // LINE app is available on both iOS and Android
    const isLineAppLikely = isMobile && (isIOS || isAndroid)
    
    // Facebook app detection is less reliable but we can make educated guesses
    // Check for Facebook app browser signatures
    const isFacebookBrowser = /fban|fbav|fbios|fb_iab|fb4a|fblc/i.test(userAgent)
    const isFacebookAppLikely = isMobile && !isFacebookBrowser // If not in FB browser, app might be available
    
    setDetection({
      isMobile,
      isIOS,
      isAndroid,
      isLineAppLikely,
      isFacebookAppLikely,
    })
  }, [])

  return detection
}

// Helper function to generate deep links
export function getOAuthDeepLink(provider: 'line' | 'facebook', clientId: string, redirectUri: string, state?: string) {
  const { isIOS, isAndroid } = useMobileDetection()
  
  if (provider === 'line') {
    // LINE OAuth deep link for mobile apps
    // LINE uses a different flow for mobile apps
    if (isIOS || isAndroid) {
      // LINE's mobile OAuth URL that should trigger app if installed
      const lineAuthUrl = new URL('https://access.line.me/oauth2/v2.1/authorize')
      lineAuthUrl.searchParams.append('response_type', 'code')
      lineAuthUrl.searchParams.append('client_id', clientId)
      lineAuthUrl.searchParams.append('redirect_uri', redirectUri)
      lineAuthUrl.searchParams.append('scope', 'profile openid email')
      if (state) lineAuthUrl.searchParams.append('state', state)
      
      // On mobile, LINE's web auth page has logic to open the app if installed
      return lineAuthUrl.toString()
    }
  } else if (provider === 'facebook') {
    // Facebook mobile app OAuth
    if (isIOS || isAndroid) {
      // Facebook's mobile OAuth URL
      const fbAuthUrl = new URL('https://www.facebook.com/v18.0/dialog/oauth')
      fbAuthUrl.searchParams.append('client_id', clientId)
      fbAuthUrl.searchParams.append('redirect_uri', redirectUri)
      fbAuthUrl.searchParams.append('scope', 'public_profile,email')
      fbAuthUrl.searchParams.append('response_type', 'code')
      if (state) fbAuthUrl.searchParams.append('state', state)
      
      return fbAuthUrl.toString()
    }
  }
  
  // Fallback to web URLs
  return null
}